import { log } from '@utils/debug'
import React from 'react'
import { useAppContext } from './use-app-context'
import { Actions, type FetchingSourceAction } from '@store/reducer'
import { useErrorBoundary } from 'react-error-boundary'
import { generateEndpointUrl, generateQueryParams } from '@utils/validation-net'
import { useAtom, useSetAtom } from 'jotai'
import { currentStepAtom, silentFetches } from '@store/atoms.ts'
import { prepareEncryptedHeader } from '@utils/crypto-utils'
import { generateHmacSHA256 } from '@utils/hmac-utils'
//import { currentStepAtom } from '@store/store'

type RequestHeaders = {
	'Content-Type':
		| 'application/json'
		| 'multipart/form-data'
		| 'application/x-www-form-urlencoded'
	// altro
	Authorization: string
	'Id-Sessione'?: string
	'Id-Sessione-Mobile'?: string
	'X-Custom-Encrypted'?: string
	'X-HMAC'?: string
}

type FetchMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

type APITriggerArgs = {
	body?: Record<string, unknown> | FormData
	query?: Record<string, string>
}

export type APIRepsonse<T> = [
	data: T | undefined | null,
	trigger: (args: APITriggerArgs) => void,
	fetching: boolean,
]

type APIRequest =
	| 'Utility/GetTypological'
	| 'Utility/FillDataFromServices'
	| 'Utility/UploadFiles'
	| 'Utility/GetAllPlaces'
	| 'Utility/GetPlaces'
	| 'Utility/GetABICABEntries'
	| 'Utility/GetWorkPlaceByAffinityCode'
	| 'Utility/ValidateCAP'
	| 'Utility/ValidateFiscalCode'
	| 'Utility/ValidateNameByCF'
	| 'Utility/ValidateSurnameByCF'
	| 'Utility/ValidateNameByFC'
	| 'Utility/ValidateSurnameByFC'
	| 'Utility/RefreshOTP'
	| 'Utility/GetSurvey'
	| 'Utility/CalculateIBAN'
	| 'Utility/ExtractDataFromCF'
	| 'Utility/GetAllSondaggioStruct'
	| 'Utility/verifyOTP'
	| 'WS/ExtractLoanInstallments'
	| 'WS/ExtractLoanInstallmentsSond'
	| 'WS/ExtractLoanInstallSondSingle'
	| 'WS/GetDocumentFolder'
	| 'DbOperations/CheckGetToken'
	| 'DbOperations/SaveStep1'
	| 'DbOperations/SaveStep2'
	| 'DbOperations/SaveStep3'
	| 'DbOperations/SaveStep4'
	| 'DbOperations/SaveStep5'
	| 'DbOperations/SaveStep6'
	| 'DbOperations/SaveStep7'
	| 'DbOperations/SaveFinalStep'
	| 'DbOperations/SaveDocuments'
	| 'DbOperations/SendDocuments'
	| 'InfoCert/InfoCertOCR'
	| 'DbOperations/GetBrowserState'
	| 'DbOperations/initMobileSession'
	| 'DbOperations/closeMobileSession'
	| 'DbOperations/validateMobileSession'
	| 'DbOperations/GetTransID'
	| 'DbOperations/GetInfoCertData'
	| 'DbOperations/reviewMobileSession'
	| 'DbOperations/GetPraticaStatus'
	| 'DbOperations/GetDocumentsUploadStatus'
	| 'DbOperations/SendToFPgesudo'
	| 'DbOperations/GetDataFromSession'
	| 'DbOperations/GetDataPraticaFpgesudo'
	| 'DbOperations/getInfoStructSim'
	| 'DbOperations/CheckGetTokenSim'
	| 'DbOperations/SalvaOffertaSim'
	| 'DbOperations/SaveRichiestaToSimulatore'
	| 'DbOperations/SaveStepSimContinua'
	| 'Utility/CheckCodAffinity'
	| 'Utility/GetAzAffinity'
	| 'WS/ExtractLoanInstallmentsSim'
	| 'DbOperations/getTrasparenza'
	| 'DbOperations/SalvaOffertaRic'
	| 'DbOperations/getNag'


export type APIParams<T> = {
	request: APIRequest
	method?: FetchMethod
	body?: Record<string, unknown>
	queryParams?: Record<string, string>
	silent?: boolean
	sleeper?: boolean
	file?: boolean
	default?: T | null
	shouldCache?: boolean
}

type AuthHeader = Partial<{
	'Id-Sessione': string
	'Id-Sessione-Mobile': string
}>

/*
 * @param {FetchMethod} method
 * @param {string} url
 * @param {string} body
 * @returns {Promise<T | undefined>}
 * @example
 * const data = await fetchData(
 * 	'GET',
 * 	'http://localhost:3001/Utility/GetTypological?type=maritalstate'
 * )
 */
async function fetchData<T>(
	method: FetchMethod,
	url: string,
	file: boolean,
	signal: AbortSignal,
	auth: AuthHeader,
	body?: Record<string, unknown> | FormData,
	passwordForHeader?: string, //add 4.1
	valueForHeader?: string, //ADD 4.1
	hmacKey?: string // NUOVO: chiave HMAC lato client
): Promise<T | undefined> {
	try {
		//ADD 4.1 set security
		const encryptedHeader = await prepareEncryptedHeader(
			valueForHeader ?? '',
			passwordForHeader ?? ''
		)

		const authHeader = Object.keys(auth).reduce((ctx, current) => {
			const key = current as keyof typeof auth
			if (auth[key] !== undefined) {
				return {
					...ctx,
					[current]: auth[key],
				}
			}
			return ctx
		}, {})

		const headers: Partial<RequestHeaders> = file
			? authHeader
			: { ...authHeader, 'Content-Type': 'application/json' }

		// ADD 4.1 Aggiungi header cifrato con un nome custom, es
		//hmac passa anche se body è un oggetto vuoto
		headers['X-Custom-Encrypted'] = encryptedHeader
		if (
			body &&
			hmacKey &&
			typeof body !== 'undefined' &&
			!(body instanceof FormData)
		) {
			const bodyStr = JSON.stringify(body)
			const hmac = await generateHmacSHA256(hmacKey, bodyStr)
			headers['X-HMAC'] = hmac
		}

		const requestInit =
			method === 'GET'
				? { headers, signal }
				: {
						method,
						headers,
						body: file ? (body as FormData) : JSON.stringify(body),
						signal,
				  }
		const response = await fetch(url, requestInit)

		const text = await response.text()

		if (!response.ok) {
			// Se il server risponde con errore scrivi errore con testo leggibile
			throw new Error(`${text}`)
		}

		try {
			return JSON.parse(text) as T
		} catch (e) {
			if (import.meta.env.DEV) {
				console.warn('Risposta non JSON:', text)
			}
			return undefined
		}
	} catch (error) {
		//  Errore di rete, tipo "Failed to fetch"
		if (error instanceof TypeError && error.message === 'Failed to fetch') {
			throw new Error(
				"Si è verificato un errore durante l'elaborazione della richiesta."
			)
		}

		// Errore per richiesta abortita
		if (error instanceof DOMException && error.name === 'AbortError') {
			throw new Error('La richiesta è stata annullata.')
		}

		//Altri errori
		throw new Error(error instanceof Error ? error.message : String(error))
	}
}

/*
 * @param {APIParams} args
 * @returns {APIResponse<T>}
 * @example
 * const [data, trigger, isLoading] = useApiRequestsHook({
 * 	request: 'Utility/GetTypological',
 * 	queryParams: { type: 'maritalstate' },
 * })
 *
 * return <Button onClick={trigger}>Refetch</Button>
 */

const useApi = <T>(args: APIParams<T>): APIRepsonse<T> => {
	const [currentStep] = useAtom(currentStepAtom)
	const setCurrentSilentFetches = useSetAtom(silentFetches)
	const [data, setData] = React.useState<T | null>(args.default || null)
	const [body, setBody] = React.useState<
		Record<string, unknown> | FormData | undefined
	>(args.body)

	const [fetching, setFetching] = React.useState(false)
	const [sleeper, setSleeping] = React.useState(args.sleeper)
	const [force, setForce] = React.useState(false)

	const { dispatch, state } = useAppContext()
	const { showBoundary } = useErrorBoundary()

	const {
		request,
		method = 'GET',
		queryParams,
		silent = false,
		file = false,
		shouldCache = true,
	} = args

	const CACHE_KEY = `${request}${generateQueryParams(queryParams ?? {})}-step${
		currentStep
	}`
	//add control
	const emptySession = 'SessioneVuota2025!'
	//ADD 4.1 PASS TO API HEADER PARAMS ID AND SESSION  WHERE REPLACE - WITH 1
	let passwordHeader = state.data?.sessione ?? emptySession
	const valueHeader = state.data?.IdRichiesta?.toString() ?? ''
	passwordHeader = passwordHeader?.replace(/-/g, '1') ?? ''

	const hmacKey = passwordHeader

	//const API_URL = import.meta.env.VITE_API_URL
	const API_URL = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL
	const [url, setUrl] = React.useState(
		generateEndpointUrl(API_URL, request, queryParams)
	)

	const trigger = ({ body, query }: APITriggerArgs) => {
		setSleeping(false)
		setForce(false)
		log('the following request has woken up: ', url, { method, body, query })
		setBody({})

		const cacheItem = sessionStorage.getItem(CACHE_KEY)

		if (cacheItem) {
			sessionStorage.removeItem(CACHE_KEY)
		}

		if (body) {
			log('body', 'set')
			setBody(body)
		}

		if (query) {
			log('query', 'set')
			setUrl(generateEndpointUrl(API_URL, request, query))
		}

		if (!query && !body) {
			log('nothing', 'set')
			setForce(true)
		}
	}

	const dispatchSource = (
		payload: FetchingSourceAction,
		silentFetch: boolean
	) => {
		if (silentFetch) {
			setCurrentSilentFetches((current) =>
				payload === 'ADD' ? current + 1 : current - 1
			)
		} else {
			dispatch({ type: Actions.UpdateFetchingSources, payload })
		}
	}

	// make the url change dynamically
	React.useEffect(
		() => setUrl(generateEndpointUrl(API_URL, request, queryParams)),
		[request]
	)

	React.useEffect(() => {
		const abortController = new AbortController()
		if (!sleeper || force) {
			dispatchSource('ADD', silent)
			// showUIProgressBar(false, silent)
			setFetching(true)
			setData(null)
			log('fetching', url, { method, body })

			const cacheItem = sessionStorage.getItem(CACHE_KEY)

			if (cacheItem) {
				setData(JSON.parse(cacheItem))
				setFetching(false)
				setForce(false)
				setSleeping(true)

				dispatchSource('REMOVE', silent)
				log('fetched-from-cache', JSON.parse(cacheItem), {
					method,
					body,
					sleeper,
					force,
				})
			} else {
				fetchData<T>(
					method,
					url,
					file,
					abortController.signal,
					{
						'Id-Sessione': state.data?.sessione?.toString(),
						'Id-Sessione-Mobile': state.mobileToken?.toString(),
					},
					body || {},
					passwordHeader,
					valueHeader,
					hmacKey
				)
					.then((e) => {
						setData((prevState) => e ?? prevState)

						setFetching(false)
						setForce(false)
						setSleeping(true)

						dispatchSource('REMOVE', silent)
						// showUIProgressBar(true, silent)

						log('fetched', e, { method, body, sleeper, force })

						if (shouldCache) {
							sessionStorage.setItem(CACHE_KEY, JSON.stringify(e))
						}
					})
					.catch((e) => {
						log('fetch error', e, { method, body })
						if (!abortController.signal.aborted) {
							showBoundary(e)
						}
					})
			}
		} else {
			log('the following request is asleep: ', url, { method, body })
		}

		return () => {
			if (fetching) {
				log('aborting', url)
				// dispatchSource('REMOVE', silent)
				// abortController.abort()
			}
		}
	}, [body, method, url, force, request])

	return [data, trigger, fetching]
}

export { useApi, fetchData }
export { type APIRequest }
